import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "readOnly", "selected", "description", "name", "size", "textLabel", "title", "type", "truncatable", "block"];
import styled, { useTheme } from 'foundations-theming/utils';
import devLogger from 'react-utils/devLogger';
import { Controllable } from '../Controllable';
import { SelectableBox } from '../SelectableBox';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { StyledIconHolder } from '../IconHolder/IconHolder';
import { StyledButton, UnstyledButton } from '../Button/PrivateButton';
import { StyledButtonLink, UnstyledButtonLink } from '../Button/PrivateButtonLink';
import { SelectableButtonProvider } from './SelectableButtonProvider';
import { StyledInnerBox } from '../SelectableBox/SelectableBox';
import { PADDING } from '../../../constants/nonTokenisedValues';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const getSelectableButtonStyles = ({
  size,
  block,
  disabled,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    maxWidth: '200px',
    textAlign: 'center'
  }, size === 'auto' && {
    display: 'inline-block',
    maxWidth: 'none',
    width: 'auto'
  }, block && {
    maxWidth: 'none',
    width: '100%'
  }, disabled && {
    cursor: 'not-allowed'
  }, {
    [`& ${StyledInnerBox}`]: {
      width: '100%'
    },
    boxShadow: 'none',
    '&:hover:not([aria-disabled="true"]):not([aria-readonly="true"])': {
      boxShadow: theme.boxShadow['300']
    }
  });
};
const StyledInner = styled.div.withConfig({
  displayName: "SelectableButton__StyledInner"
})(props => {
  const theme = props.theme;
  const shorthandSize = toShorthandSize(props.$size || 'sm');
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  }, shorthandSize === 'lg' && {
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '180px',
    padding: theme.space[300]
  }, shorthandSize === 'sm' && {
    gap: theme.space[400],
    minHeight: '56px',
    padding: theme.space[300]
  }, props.$size === 'auto' && {
    gap: theme.space[400],
    padding: theme.space[400]
  }, props.$type !== 'default' && props.$selectionMarkLocation === 'left' && {
    paddingInlineStart: theme.space[0]
  }, {
    '& img': {
      transform: 'translateZ(0)',
      maxWidth: '100%'
    },
    '& svg': {
      marginLeft: theme.space[100]
    },
    // Don't apply marginLeft to icons rendered by IconCircle (renders IconHolder) and IconButton (renders Button).
    // Note: this is a net new function that does not exist in UISelectableButton.
    [`& ${StyledIconHolder}, & ${StyledButton}, & ${UnstyledButton}, & ${StyledButtonLink}, & ${UnstyledButtonLink}`]: {
      '& svg': {
        marginLeft: theme.space[0]
      }
    }
  });
});
const StyledLabel = styled.div.withConfig({
  displayName: "SelectableButton__StyledLabel"
})(props => {
  const theme = props.theme;
  const shorthandSize = toShorthandSize(props.$size || 'sm');
  return Object.assign({
    display: 'block',
    whiteSpace: 'normal',
    width: '100%'
  }, theme.typography['heading-100'], shorthandSize === 'lg' && {
    paddingTop: theme.space[400],
    paddingBottom: theme.space[200]
  }, shorthandSize === 'sm' && Object.assign({
    marginLeft: 0,
    marginRight: 0
  }, props.$truncatable && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }, props.$type !== 'default' && {
    paddingLeft: PADDING.SELECTABLE_BUTTON_PADDING_LEFT
  }), {
    lineHeight: theme.typography['heading-200'].lineHeight
  });
});
const StyledDescription = styled.span.withConfig({
  displayName: "SelectableButton__StyledDescription"
})(props => {
  const theme = props.theme;
  return Object.assign({}, theme.typography['body-100'], {
    display: 'block',
    whiteSpace: 'normal',
    marginTop: theme.space[100],
    color: theme.color['text-core-subtle']
  });
});
const Title = styled.span.withConfig({
  displayName: "SelectableButton__Title"
})(({
  theme
}) => Object.assign({}, theme.typography['heading-100'], {
  display: 'block',
  whiteSpace: 'normal',
  width: '100%',
  color: theme.color['text-core-default']
}));
const InternalSelectableButton = _ref => {
  let {
      children,
      disabled = false,
      readOnly = false,
      selected = false,
      description,
      name,
      size = 'sm',
      textLabel,
      title,
      type = 'default',
      truncatable = true,
      block = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  if (process.env.NODE_ENV !== 'production') {
    if (textLabel == null && title == null && description == null && children == null) {
      devLogger.warn({
        message: 'SelectableButton: Should provide either text through title and description, or children.',
        key: 'SelectableButton: content'
      });
    }
    if (textLabel) {
      devLogger.warn({
        message: `SelectableButton: The \`textLabel\` prop is deprecated. Use the \`title\` and \`description\` props instead.`,
        key: 'SelectableButton-textLabel'
      });
    }
    if (type === 'radio' && !name) {
      devLogger.warn({
        message: 'SelectableButton: If type is set to radio you must provide a name',
        key: 'SelectableButton: no name value'
      });
    }
  }
  const shorthandSize = toShorthandSize(size);
  let computedSelectionMarkLocation;
  if (type !== 'default') {
    computedSelectionMarkLocation = shorthandSize === 'lg' ? 'top-right' : 'left';
  }
  const selectableButtonStyles = getSelectableButtonStyles({
    size,
    block,
    disabled,
    theme
  });
  return /*#__PURE__*/_jsx(SelectableButtonProvider, {
    selectableButtonStyleOverrides: selectableButtonStyles,
    children: /*#__PURE__*/_jsx(SelectableBox, Object.assign({
      disabled: disabled,
      readOnly: readOnly,
      selected: selected,
      name: type === 'default' ? undefined : name,
      padded: false,
      selectionMarkLocation: computedSelectionMarkLocation,
      type: type,
      block: block
    }, rest, {
      children: /*#__PURE__*/_jsxs(StyledInner, {
        $size: size,
        $type: type,
        $selectionMarkLocation: computedSelectionMarkLocation,
        children: [children, textLabel ? /*#__PURE__*/_jsx(StyledLabel, {
          as: "span",
          $size: size,
          $truncatable: truncatable,
          $type: type,
          children: textLabel
        }) : null, !textLabel && (title || description) && /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsxs(StyledLabel, {
            as: title && description ? 'div' : 'span',
            $size: size,
            $truncatable: truncatable,
            $type: type,
            children: [title && /*#__PURE__*/_jsx(Title, {
              children: title
            }), description && /*#__PURE__*/_jsx(StyledDescription, {
              $size: size,
              children: description
            })]
          })
        })]
      })
    }))
  });
};
const defaultProps = {
  selected: false
};
export const SelectableButton = Controllable(InternalSelectableButton, ['selected'], defaultProps);
SelectableButton.displayName = 'SelectableButton';